import React, { useEffect, useRef, useState } from "react";
import Datatable from "../../components/shared/datatable/Datatable";
import { EyeView } from "../../svgFiles/EyeView";
import { Invoice } from "../../svgFiles/Invoice";
import { OrangeArrow } from "../../svgFiles/OrangeArrow";
import { Dropdown, Overlay, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router";
import {
  useGetExpensesQuery,
  useGetExpenseByIdQuery,
  useUploadExpenseInvoiceMutation,
  useUploadExpenseReceiptMutation,
  useRemoveExpenseInvoiceMutation,
  useRemoveExpenseReceiptMutation,
} from "./expenseApi";
import { formatNumber, showToast } from "../../helpers/Utils";
import { Info } from "../../svgFiles/Info";
import { Print } from "../../svgFiles/Print";
import BillViewModal from "../../components/shared/modalContent/BillViewModal";
import useTranslator from "../../hooks/useTranslator";
import { UploadSimpleSvg } from "../../svgFiles/UploadSimpleSvg";
import CommonDeleteModal from "../../components/shared/modalLayout/CommonDeleteModal";

const BillingReport = () => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const [filterParams, setFilterParams] = useState({
    page: 1,
    per_page: 10,
    status: "paid", // ✅ Only fetch paid bills
  });

  // State for bill view modal
  const [showBillViewModal, setShowBillViewModal] = useState(false);
  const [selectedBillId, setSelectedBillId] = useState(null);
  const { data: billQueryData, isLoading: isLoadingBillDetails } =
    useGetExpenseByIdQuery(selectedBillId, { skip: !selectedBillId });
  const [selectedBillData, setSelectedBillData] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const {
    data: expenseData,
    isLoading,
    refetch,
    isFetching,
  } = useGetExpensesQuery({
    ...filterParams,
    status: "paid", // ✅ force it here too
  });

  const [uploadExpenseInvoice, { isLoading: isUploadingInvoice }] =
    useUploadExpenseInvoiceMutation();

  const [uploadExpenseReceipt, { isLoading: isUploadingReceipt }] =
    useUploadExpenseReceiptMutation();

  const [removeExpenseInvoice, { isLoading: isRemovingInvoice }] =
    useRemoveExpenseInvoiceMutation();

  const [removeExpenseReceipt, { isLoading: isRemovingReceipt }] =
    useRemoveExpenseReceiptMutation();

  const [selectedInvoiceRecordId, setSelectedInvoiceRecordId] = useState(null);
  const [selectedReceiptRecordId, setSelectedReceiptRecordId] = useState(null);
  const [showInvoiceDelModal, setShowInvoiceDelModal] = useState(false);
  const [delInvoiceRecordId, setDelInvoiceRecordId] = useState(null);
  const [showReceiptDelModal, setShowReceiptDelModal] = useState(false);
  const [delReceiptRecordId, setDelReceiptRecordId] = useState(null);

  const fileInputRefs = useRef({});
  const receiptFileInputRefs = useRef({});

  useEffect(() => {
    if (billQueryData?.data) {
      setSelectedBillData(billQueryData.data);
    }
  }, [billQueryData]);

  const [activeTooltipId, setActiveTooltipId] = useState(null);

  const tooltipRefs = React.useMemo(() => {
    const refs = {};
    if (expenseData?.data?.data) {
      expenseData.data.data.forEach((row) => {
        refs[row.id] = React.createRef();
      });
    }
    return refs;
  }, [expenseData?.data?.data]);

  const showTooltip = (id) => setActiveTooltipId(id);
  const hideTooltip = () => setActiveTooltipId(null);

  // Handle view expense - open modal with expense details
  const handleViewExpense = (id) => {
    setSelectedBillId(id);
    setShowBillViewModal(true);
  };

  // Close bill view modal
  const handleCloseBillViewModal = () => {
    setShowBillViewModal(false);
    setSelectedBillId(null);
    setSelectedBillData(null);
  };

  const handlePrintPDF = (pdfUrl) => {
    if (!pdfUrl) {
      showToast(_t("pdf_not_available", "The PDF is not available. Please try again later."), "error");
      return;
    }
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.target = "_blank";
    link.download = "invoice.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      label: _t("bill_number", "Bill Number"),
      accessor: "bill_number",
      cell: ({ row }) => <b className="text-dark">#{row.bill_number}</b>,
    },
    {
      label: _t("vendor", "Vendor"),
      accessor: "vendor_name",
    },
    {
      label: _t("expense_type", "Expense Type"),
      accessor: "expense_type",
      cell: ({ row }) => (
        <span>
          {row.items && row.items.length > 0
            ? _t(row.items[0].expense_type , row.items[0].expense_type)
            : "N/A"}
          <span
            className="cursor-pointer ps-2"
            ref={tooltipRefs[row.id]}
            onClick={() =>
              activeTooltipId === row.id ? hideTooltip() : showTooltip(row.id)
            }
            onMouseEnter={() => showTooltip(row.id)}
            onMouseLeave={() => hideTooltip()}
          >
            {Info}
          </span>
          <Overlay
            target={tooltipRefs[row.id]?.current}
            show={activeTooltipId === row.id}
            placement="bottom"
            delay={{ show: 0, hide: 500 }}
            rootClose={false}
          >
            {(props) => (
              <Tooltip
                id={`tooltip-${row.id}`}
                {...props}
                className="custom-tooltip"
              >
                <div className="tooltip-content">
                  <div className="tooltip-header">
                    {_t("expense_type_and_amount", "Expense Type & Amount")}
                  </div>
                  <ul className="tooltip-list">
                    {row.items?.map((item, index) => (
                      <li key={index}>
                        <span>{_t(item.expense_type , item.expense_type)}</span>
                        <span className="tooltip-amount">
                          ${formatNumber(item.amount || 0)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Tooltip>
            )}
          </Overlay>
        </span>
      ),
    },
    {
      label: _t("bill_date", "Bill Date"),
      accessor: "bill_date",
      cell: ({ row }) => <span>{row.bill_date}</span>,
    },
    {
      label: _t("due_date", "Due Date"),
      accessor: "due_date",
      cell: ({ row }) => <span>{row.due_date}</span>,
    },
    {
      label: _t("bill_amount", "Bill Amount"),
      accessor: "total_amount",
      cell: ({ row }) => <span>${formatNumber(row.total_amount || 0)}</span>,
    },
    {
      label: _t("is_registered", "Is Registered"),
      accessor: "is_registered",
      cell: ({ row }) => (
        <span>
          {row.is_registered == "1" ? _t("yes", "Yes") : _t("no", "No")}
        </span>
      ),
    },
    {
      label: _t("invoice", "Invoice"),
      accessor: "invoice_file_url",
      cell: ({ row }) => {
        if (row.invoice_file_url) {
          return (
            <div className="position-relative file_view">
              <a
                href={row.invoice_file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer"
              >
                {Invoice}
              </a>
              <button
                type="button"
                onClick={() => {
                  setDelInvoiceRecordId(row.id);
                  setShowInvoiceDelModal(true);
                }}
              >
                X
              </button>
            </div>
          );
        } else {
          if (isUploadingInvoice && selectedInvoiceRecordId == row.id) {
            return `${_t("uploading", "Uploading")}...`;
          }
          return (
            <div className="position-relative file_view cursor-pointer">
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFileChange.bind(this, row.id)}
                ref={(el) => (fileInputRefs.current[row.id] = el)}
                className=""
              />
              <span>
                <UploadSimpleSvg />
              </span>
            </div>
          );
        }
      },
    },
    {
      label: _t("receipt", "Receipt"),
      accessor: "receipt_file_url",
      cell: ({ row }) => {
        if (row.receipt_file_url) {
          return (
            <div className="position-relative file_view">
              <a
                href={row.receipt_file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer"
              >
                {Invoice}
              </a>
              <button
                type="button"
                onClick={() => {
                  setDelReceiptRecordId(row.id);
                  setShowReceiptDelModal(true);
                }}
              >
                X
              </button>
            </div>
          );
        } else {
          if (isUploadingReceipt && selectedReceiptRecordId == row.id) {
            return `${_t("uploading", "Uploading")}...`;
          }
          return (
            <div className="position-relative file_view cursor-pointer">
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/jpg"
                onChange={handleReceiptFileChange.bind(this, row.id)}
                ref={(el) => (receiptFileInputRefs.current[row.id] = el)}
                className=""
              />
              <span>
                <UploadSimpleSvg />
              </span>
            </div>
          );
        }
      },
    },
    {
      label: _t("action", "Action"),
      accessor: "action",
      cell: ({ row }) => (
        <div className="d-flex gap-3">
          <span
            className="cursor-pointer"
            onClick={() => handleViewExpense(row.id)}
            title={_t("view_details", "View Details")}
          >
            {EyeView}
          </span>
          <span
            className="cursor-pointer"
            onClick={() => handlePrintPDF(row.invoice_file_url)}
            title={_t("print_invoice", "Print Invoice")}
          >
            {Print}
          </span>
        </div>
      ),
    },
  ];

  const handlePageChange = (page) => {
    setFilterParams((prev) => ({
      ...prev,
      page,
    }));
  };

  const handleFilterChange = (filters) => {
    setFilterParams((prev) => ({
      ...prev,
      ...filters,
      status: "paid", // ✅ Always keep status filter on
    }));
  };

  const handleFileChange = async (id, event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        showToast(
          _t("only_pdf_allowed", "Only PDF files are allowed."),
          "error"
        );
        return;
      }
      try {
        setSelectedInvoiceRecordId(id);
        const formData = new FormData();
        formData.append("invoice", file);
        const result = await uploadExpenseInvoice({
          id,
          requestBody: formData,
        }).unwrap();
      } catch (error) {
      } finally {
        setSelectedInvoiceRecordId(null);
        const inputEl = fileInputRefs.current[id];
        if (inputEl) {
          inputEl.value = "";
        }
      }
    }
  };

  const handleReceiptFileChange = async (id, event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/jpg",
      ];

      const MAX_SIZE_MB = 5;
      const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

      if (!allowedTypes.includes(file.type)) {
        showToast(
          _t(
            "only_pdf_or_image_allowed",
            "Only PDF or image files are allowed."
          ),
          "error"
        );
        return;
      }

      if (file.size > MAX_SIZE_BYTES) {
        showToast(
          _t(
            "file_too_large",
            `File must not be larger than ${MAX_SIZE_MB}MB.`
          ),
          "error"
        );
        return;
      }

      try {
        setSelectedReceiptRecordId(id);
        const formData = new FormData();
        formData.append("receipt", file);

        const result = await uploadExpenseReceipt({
          id,
          requestBody: formData,
        }).unwrap();
      } catch (error) {
        // Handle error (optional)
      } finally {
        setSelectedReceiptRecordId(null);
        const inputEl = receiptFileInputRefs.current[id];
        if (inputEl) {
          inputEl.value = "";
        }
      }
    }
  };

  const deleteInvoiceHandler = async () => {
    // Handle delete invoice logic here
    if (delInvoiceRecordId) {
      try {
        await removeExpenseInvoice(delInvoiceRecordId).unwrap();
      } catch (error) {
      } finally {
        setDelInvoiceRecordId(null);
        setShowInvoiceDelModal(false);
      }
    }
  };

  const deleteReceiptHandler = async () => {
    // Handle delete receipt logic here
    if (delReceiptRecordId) {
      try {
        await removeExpenseReceipt(delReceiptRecordId).unwrap();
      } catch (error) {
      } finally {
        setDelReceiptRecordId(null);
        setShowReceiptDelModal(false);
      }
    }
  };

  return (
    <>
      {/* Bill View Modal */}
      <BillViewModal
        show={showBillViewModal}
        handleClose={handleCloseBillViewModal}
        billData={selectedBillData}
      />

      <CommonDeleteModal
        show={showInvoiceDelModal}
        handleClose={() => {
          setShowInvoiceDelModal(false);
        }}
        title={_t("confirm_delete", "Confirm Delete")}
        message={`${_t(
          "are_you_sure_you_want_to_delete",
          "Are you sure you want to delete"
        )} ?`}
        onConfirm={deleteInvoiceHandler}
        isLoading={isRemovingInvoice}
      />

      <CommonDeleteModal
        show={showReceiptDelModal}
        handleClose={() => {
          setShowReceiptDelModal(false);
        }}
        title={_t("confirm_delete", "Confirm Delete")}
        message={`${_t(
          "are_you_sure_you_want_to_delete",
          "Are you sure you want to delete"
        )} ?`}
        onConfirm={deleteReceiptHandler}
        isLoading={isRemovingReceipt}
      />

      <Datatable
        title={_t("paid_bill_report", "Paid Bill Report")}
        columns={columns}
        tableData={expenseData?.data || { data: [] }}
        pagination
        isLoading={isLoading}
        showFilterButton={true}
        showCount={true}
        page={filterParams.page}
        onPageChange={handlePageChange}
        showFilter={handleFilterChange}
        filterModule="billListing"
        isLoading={isLoading}
      />
    </>
  );
};

export default BillingReport;
